/*
 * bb-ritual.js — desktop scrollytelling logic for sections/product-ritual.liquid
 * ("The Ritual"). Loaded defer from the section.
 *
 * SCOPE
 *   This file covers ONLY the desktop media crossfade: an IntersectionObserver
 *   flags the centered step block and toggles .is-active on the matching image
 *   frame (opacity crossfade — NO pin, NO scrub, nothing translates on scroll;
 *   owner rule: content stays put on scroll). The mobile slider (counter,
 *   progress rail, arrows, keyboard, scroll sync) is handled by the shared
 *   <bb-carousel> custom element and is NOT touched here.
 *
 * WHY A CUSTOM ELEMENT ISN'T USED
 *   bb-carousel is a horizontal scroll-snap component; the desktop moment is a
 *   vertical IntersectionObserver crossfade of a static (in-flow) media panel.
 *   That is a different mechanism, so it stays as a small purpose-built
 *   controller.
 *
 * LIFECYCLE (theme-editor safe)
 *   - Idempotent per root: a WeakMap of active controllers guards double-init.
 *   - Each controller tears down its IntersectionObserver AND its matchMedia
 *     change listeners in destroy() — nothing outlives the section.
 *   - Under Shopify.designMode we re-init on 'shopify:section:load' and clean
 *     up on 'shopify:section:unload' (scoped to the reloaded section id).
 *   - matchMedia listeners are removed on destroy (the original inline script
 *     leaked these on every editor re-render; that leak is fixed here).
 */
(function () {
  'use strict';

  var SELECTOR = '[data-rt-grid]';
  var controllers = new WeakMap();

  function slice(list) {
    return Array.prototype.slice.call(list || []);
  }

  function Ritual(root) {
    this.root = root;
    this.frames = slice(root.querySelectorAll('[data-rt-frame]'));
    this.steps = slice(root.querySelectorAll('[data-rt-step]'));
    this.observer = null;
    this.current = 0;

    this.mqDesktop = window.matchMedia('(min-width: 990px)');
    this.mqMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    var self = this;
    this._onChange = function () { self.setup(); };

    // matchMedia change wiring (with legacy fallback).
    if (this.mqDesktop.addEventListener) {
      this.mqDesktop.addEventListener('change', this._onChange);
      this.mqMotion.addEventListener('change', this._onChange);
      this._mqModern = true;
    } else if (this.mqDesktop.addListener) {
      this.mqDesktop.addListener(this._onChange);
      this.mqMotion.addListener(this._onChange);
      this._mqModern = false;
    }

    this.setup();
  }

  Ritual.prototype.setActive = function (idx) {
    for (var i = 0; i < this.frames.length; i++) {
      this.frames[i].classList.toggle('is-active', i === idx);
    }
  };

  Ritual.prototype.teardownObserver = function () {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  };

  Ritual.prototype.setup = function () {
    this.teardownObserver();

    // Below 990px or reduced motion: no pin/observer effect.
    // Reduced motion already shows the first frame via CSS; ensure it's flagged.
    if (!this.mqDesktop.matches || this.mqMotion.matches) {
      if (this.mqMotion.matches) this.setActive(0);
      return;
    }

    this.current = 0;
    var self = this;
    // Centered-band observer: a step is "active" while its center is near the
    // middle of the viewport.
    this.observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var e = entries[i];
        if (e.isIntersecting) {
          var idx = parseInt(e.target.getAttribute('data-rt-step'), 10);
          if (idx !== self.current) {
            self.current = idx;
            self.setActive(idx);
          }
        }
      }
    }, {
      // narrow horizontal band through the vertical middle of the viewport
      rootMargin: '-45% 0px -45% 0px',
      threshold: 0
    });

    for (var s = 0; s < this.steps.length; s++) {
      this.observer.observe(this.steps[s]);
    }
    this.setActive(this.current);
  };

  Ritual.prototype.destroy = function () {
    this.teardownObserver();
    if (this._mqModern) {
      this.mqDesktop.removeEventListener('change', this._onChange);
      this.mqMotion.removeEventListener('change', this._onChange);
    } else if (this.mqDesktop.removeListener) {
      this.mqDesktop.removeListener(this._onChange);
      this.mqMotion.removeListener(this._onChange);
    }
  };

  function init(root) {
    if (!root || controllers.has(root)) return;
    controllers.set(root, new Ritual(root));
  }

  function destroy(root) {
    if (!root) return;
    var c = controllers.get(root);
    if (c) {
      c.destroy();
      controllers.delete(root);
    }
  }

  function initAll(scope) {
    var grids = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < grids.length; i++) init(grids[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme-editor lifecycle: only wire when actually in the editor.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      initAll(e.target);
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var grids = e.target.querySelectorAll(SELECTOR);
      for (var i = 0; i < grids.length; i++) destroy(grids[i]);
    });
  }
})();
